"use client";

import { useEffect, useMemo, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { AuthDialog } from "@/components/auth/AuthDialog";
import type { Dictionary, Locale } from "@/i18n/dictionaries";
import type { ProductDetailDto, ProductOptionDto } from "@/lib/product-types";
import {
  createProductRenderCartSnapshot,
  PRODUCT_RENDER_OPTIONS_EVENT,
  rememberProductRenderSelection,
  type ProductRenderSelection,
} from "@/lib/product-render-client";
import { localizeProductOptionLabel } from "@/lib/product-option-i18n";
import styles from "./productDetail.module.css";

type Labels = {
  category: string;
  selectMaterial: string;
  selectCraft: string;
  decrease: string;
  increase: string;
  addToCart: string;
  favorite: string;
  share: string;
  priceUnavailable: string;
  signInRequired: string;
};

type ProductInteractivePanelProps = {
  detail: ProductDetailDto;
  labels: Labels;
  authLabels: Dictionary["authPage"];
  currency: string;
  locale: Locale;
};

type PriceState = {
  unitPrice?: number;
  totalPrice?: number;
  loading: boolean;
  message?: string;
};

type ProductActionResult = {
  ok: boolean;
  status: string;
  message: string;
  data?: {
    cartId?: string | number;
    renderSnapshotId?: string | null;
    renderCartBindingId?: string | null;
    renderBindingStatus?: string | null;
  };
};

function localizedProductMessage(locale: Locale, key: "favoriteAdded" | "addedToCart" | "shareCopied" | "shareFailed" | "actionFailed") {
  const zh = locale === "zh-CN";
  const messages = {
    favoriteAdded: zh ? "已收藏。" : "Favorited.",
    addedToCart: zh ? "已加入购物车。" : "Added to cart.",
    shareCopied: zh ? "分享链接已复制。" : "Share link copied.",
    shareFailed: zh ? "无法复制分享链接，请稍后重试。" : "Unable to copy share link. Please try again.",
    actionFailed: zh ? "商品服务暂不可用，请稍后重试。" : "The product service is temporarily unavailable. Please try again.",
  };
  return messages[key];
}

function optionClass(option: ProductOptionDto, selectedId?: string, disabledIds: string[] = []) {
  const classes = [styles.optionChip];
  if (option.id === selectedId) {
    classes.push(styles.optionChipActive);
  }
  if (disabledIds.includes(option.id)) {
    classes.push(styles.optionChipDisabled);
  }
  return classes.join(" ");
}

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(body)
  });
  return (await response.json()) as T;
}

export function ProductInteractivePanel({ detail, labels, authLabels, currency, locale }: Readonly<ProductInteractivePanelProps>) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [materialId, setMaterialId] = useState(detail.commerce.defaultMaterialId || detail.commerce.materials[0]?.id || "");
  const [craftId, setCraftId] = useState(detail.commerce.defaultCraftId || detail.commerce.crafts[0]?.id || "");
  const [quantity, setQuantity] = useState(1);
  const [disabledMaterials, setDisabledMaterials] = useState<string[]>([]);
  const [disabledCrafts, setDisabledCrafts] = useState<string[]>([]);
  const [price, setPrice] = useState<PriceState>({ loading: false });
  const [message, setMessage] = useState("");
  const [authOpen, setAuthOpen] = useState(false);

  const canPrice = Boolean(materialId && craftId && detail.commerce.volume && detail.commerce.modelArea);
  const selectedMaterial = useMemo(() => detail.commerce.materials.find((item) => item.id === materialId), [detail.commerce.materials, materialId]);
  const selectedCraft = useMemo(() => detail.commerce.crafts.find((item) => item.id === craftId), [detail.commerce.crafts, craftId]);

  useEffect(() => {
    const renderSelection: ProductRenderSelection = {
      goodsId: detail.goodsId,
      materialId,
      materialLabel: selectedMaterial?.label,
      materialRenderCode: selectedMaterial?.renderCode,
      craftId,
      craftLabel: selectedCraft?.label,
      craftRenderCode: selectedCraft?.renderCode,
    };

    rememberProductRenderSelection(renderSelection);
    window.dispatchEvent(new CustomEvent<ProductRenderSelection>(PRODUCT_RENDER_OPTIONS_EVENT, { detail: renderSelection }));
  }, [
    craftId,
    detail.goodsId,
    materialId,
    selectedCraft?.label,
    selectedCraft?.renderCode,
    selectedMaterial?.label,
    selectedMaterial?.renderCode,
  ]);

  function currentReturnUrl() {
    const query = searchParams.toString();
    return query ? `${pathname}?${query}` : pathname;
  }

  useEffect(() => {
    if (!materialId) {
      setDisabledCrafts([]);
      return;
    }
    postJson<{ ok: boolean; disabled?: string[]; data?: { disabled?: string[] } }>(`/api/products/${detail.goodsId}/sku`, {
      propertyValueId: materialId,
      type: "1"
    })
      .then((result) => setDisabledCrafts(result.data?.disabled || result.disabled || []))
      .catch(() => setDisabledCrafts([]));
  }, [detail.goodsId, materialId]);

  useEffect(() => {
    if (!craftId) {
      setDisabledMaterials([]);
      return;
    }
    postJson<{ ok: boolean; disabled?: string[]; data?: { disabled?: string[] } }>(`/api/products/${detail.goodsId}/sku`, {
      propertyValueId: craftId,
      type: "2"
    })
      .then((result) => setDisabledMaterials(result.data?.disabled || result.disabled || []))
      .catch(() => setDisabledMaterials([]));
  }, [craftId, detail.goodsId]);

  useEffect(() => {
    if (!canPrice) {
      setPrice({ loading: false, message: labels.priceUnavailable });
      return;
    }

    setPrice((current) => ({ ...current, loading: true }));
    postJson<{ ok: boolean; message?: string; unitPrice?: number; totalPrice?: number; data?: { unitPrice?: number; totalPrice?: number } }>(`/api/products/${detail.goodsId}/price`, {
      materialId,
      craftId,
      quantity,
      volume: detail.commerce.volume,
      modelArea: detail.commerce.modelArea
    })
      .then((result) => {
        setPrice({
          loading: false,
          unitPrice: result.data?.unitPrice ?? result.unitPrice,
          totalPrice: result.data?.totalPrice ?? result.totalPrice,
          message: result.ok ? undefined : labels.priceUnavailable
        });
      })
      .catch(() => setPrice({ loading: false, message: labels.priceUnavailable }));
  }, [canPrice, craftId, detail.commerce.modelArea, detail.commerce.volume, detail.goodsId, labels.priceUnavailable, materialId, quantity]);

  async function runAction(path: "favorite" | "cart", body: unknown) {
    setMessage("");
    let result: ProductActionResult;
    try {
      result = await postJson<ProductActionResult>(`/api/products/${detail.goodsId}/${path}`, body);
    } catch {
      setMessage(localizedProductMessage(locale, "actionFailed"));
      return null;
    }
    if (result.status === "auth-required") {
      setMessage(labels.signInRequired);
      setAuthOpen(true);
      return result;
    }
    if (path === "favorite" && result.ok) {
      setMessage(localizedProductMessage(locale, "favoriteAdded"));
    } else if (path === "cart" && result.ok) {
      setMessage(localizedProductMessage(locale, "addedToCart"));
    } else {
      setMessage(localizedProductMessage(locale, "actionFailed"));
    }
    if (path === "cart" && result.ok) {
      window.dispatchEvent(new Event("cart:refresh"));
    }
    return result;
  }

  async function shareProduct() {
    setMessage("");
    const query = searchParams.toString();
    const path = query ? `${pathname}?${query}` : pathname;
    const url = `${window.location.origin}${path}`;

    try {
      if (navigator.share) {
        await navigator.share({ title: detail.title, url });
        return;
      }

      await navigator.clipboard.writeText(url);
      setMessage(localizedProductMessage(locale, "shareCopied"));
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      setMessage(localizedProductMessage(locale, "shareFailed"));
    }
  }

  return (
    <aside className={styles.purchasePanel}>
      <p className={styles.designer}>{detail.designerName || labels.category}</p>
      <h1>{detail.title}</h1>

      <div className={styles.optionBlock}>
        <h2>{labels.selectMaterial}</h2>
        <div className={styles.chips}>
          {detail.commerce.materials.map((option) => (
            <button
              key={option.id}
              type="button"
              className={optionClass(option, materialId, disabledMaterials)}
              disabled={disabledMaterials.includes(option.id)}
              onClick={() => setMaterialId(option.id)}
            >
              {localizeProductOptionLabel(option.label, locale)}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.optionBlock}>
        <h2>{labels.selectCraft}</h2>
        <div className={styles.craftChips}>
          {detail.commerce.crafts.map((option) => (
            <button
              key={option.id}
              type="button"
              className={optionClass(option, craftId, disabledCrafts)}
              disabled={disabledCrafts.includes(option.id)}
              onClick={() => setCraftId(option.id)}
            >
              {localizeProductOptionLabel(option.label, locale)}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.quantity}>
        <button type="button" aria-label={labels.decrease} onClick={() => setQuantity((value) => Math.max(1, value - 1))}>
          -
        </button>
        <span>{quantity}</span>
        <button type="button" aria-label={labels.increase} onClick={() => setQuantity((value) => Math.min(99, value + 1))}>
          +
        </button>
      </div>

      <p className={styles.price}>
        {price.loading
          ? "..."
          : price.totalPrice
            ? `${currency}${price.totalPrice.toFixed(2)}`
            : price.message || labels.priceUnavailable}
      </p>
      <p className={styles.mockOnly}>
        {localizeProductOptionLabel(selectedMaterial?.label, locale) || "-"} / {localizeProductOptionLabel(selectedCraft?.label, locale) || "-"}
      </p>

      <button
        type="button"
        className={styles.cartButton}
        disabled={!price.unitPrice || !detail.commerce.modelId || !detail.commerce.modelName}
        onClick={() =>
          runAction("cart", {
            modelId: detail.commerce.modelId,
            modelName: detail.commerce.modelName,
            materialId,
            craftId,
            quantity,
            shopPrice: price.unitPrice,
            renderSnapshot: createProductRenderCartSnapshot(detail.goodsId, {
              materialId,
              materialLabel: selectedMaterial?.label,
              craftId,
              craftLabel: selectedCraft?.label,
              quantity,
              unitPrice: price.unitPrice,
              totalPrice: price.totalPrice,
            })
          })
        }
      >
        {labels.addToCart}
      </button>

      <div className={styles.actions}>
        <button type="button" onClick={() => runAction("favorite", { action: "add" })}>
          {labels.favorite}
        </button>
        <button type="button" onClick={shareProduct}>
          {labels.share}
        </button>
      </div>

      {message ? <p className={styles.actionMessage}>{message}</p> : null}
      {authOpen ? (
        <AuthDialog
          locale={locale}
          labels={authLabels}
          mode="login"
          returnUrl={currentReturnUrl()}
          onClose={() => setAuthOpen(false)}
        />
      ) : null}
    </aside>
  );
}
